/* File: assets/lens-hotspots.js */

(() => {
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n))

  const initCompareEl = (wrap) => {
    const range = wrap.querySelector("[data-lh-range]")
    if (!range) return

    const def = wrap.getAttribute("data-default")
    const start = def ? Number(def) : Number(range.value || 50)

    const apply = (v) => wrap.style.setProperty("--pos", clamp(v, 0, 100) + "%")

    range.value = String(start)
    apply(start)

    range.addEventListener("input", (e) => apply(Number(e.target.value)))
  }

  const initAllCompares = (root) => {
    root.querySelectorAll("[data-lh-compare]").forEach(initCompareEl)
  }

  const animateOpen = (pop) => {
    pop.hidden = false
    pop.classList.remove("is-closing")
    pop.classList.remove("is-open")

    pop.getBoundingClientRect()

    requestAnimationFrame(() => {
      pop.classList.add("is-open")
    })
  }

  const animateClose = (pop, immediate = false) => {
    if (immediate) {
      pop.classList.remove("is-open")
      pop.classList.remove("is-closing")
      pop.hidden = true
      return
    }

    if (pop.hidden) return

    pop.classList.add("is-closing")
    pop.classList.remove("is-open")

    const done = () => {
      pop.hidden = true
      pop.classList.remove("is-closing")
      pop.removeEventListener("transitionend", onEnd)
    }

    const onEnd = (e) => {
      if (e.target !== pop) return
      done()
    }

    pop.addEventListener("transitionend", onEnd)
    window.setTimeout(done, 320)
  }

  const closeAllPopovers = (section, immediate = false) => {
    section.querySelectorAll("[data-lh-hotspot]").forEach((b) => b.setAttribute("aria-expanded", "false"))
    section.querySelectorAll("[data-lh-popover]").forEach((p) => {
      p.style.left = ""
      p.style.top = ""
      animateClose(p, immediate)
    })
  }

  const placePopover = (section, btn, pop) => {
    const stage = section.querySelector(".lh__stage--desktop")
    if (!stage) return

    const stageRect = stage.getBoundingClientRect()
    const btnRect = btn.getBoundingClientRect()

    pop.style.left = "0px"
    pop.style.top = "0px"

    if (pop.hidden) animateOpen(pop)
    else {
      pop.classList.remove("is-closing")
      pop.classList.add("is-open")
    }

    const popRect = pop.getBoundingClientRect()
    const gap = 12
    const preferRight = btnRect.left < stageRect.left + stageRect.width / 2

    let left = preferRight
      ? btnRect.right - stageRect.left + gap
      : btnRect.left - stageRect.left - popRect.width - gap

    let top = btnRect.top - stageRect.top - 120

    left = clamp(left, 8, stageRect.width - popRect.width - 8)
    top = clamp(top, 8, stageRect.height - popRect.height - 8)

    pop.style.left = left + "px"
    pop.style.top = top + "px"
  }

  const setupHotspots = (section) => {
    section.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-lh-hotspot]")
      if (btn) {
        e.preventDefault()

        const id = btn.getAttribute("data-pop-id")
        const pop = id ? section.querySelector("#" + CSS.escape(id)) : null
        if (!pop) return

        const wasOpen = btn.getAttribute("aria-expanded") === "true"
        closeAllPopovers(section, true)
        if (wasOpen) return

        btn.setAttribute("aria-expanded", "true")
        placePopover(section, btn, pop)

        window.setTimeout(() => {
          try { pop.focus({ preventScroll: true }) } catch (err) {}
        }, 10)

        return
      }

      const closeBtn = e.target.closest("[data-lh-close]")
      if (closeBtn) {
        e.preventDefault()
        closeAllPopovers(section, false)
        return
      }

      const insidePop = e.target.closest("[data-lh-popover]")
      const insideHotspot = e.target.closest("[data-lh-hotspot]")
      if (!insidePop && !insideHotspot) closeAllPopovers(section, false)
    })

    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeAllPopovers(section, false)
    })

    window.addEventListener("resize", () => {
      const openBtn = section.querySelector('[data-lh-hotspot][aria-expanded="true"]')
      if (!openBtn) return

      const id = openBtn.getAttribute("data-pop-id")
      const pop = id ? section.querySelector("#" + CSS.escape(id)) : null
      if (!pop) return

      placePopover(section, openBtn, pop)
    })
  }

  const setupCarousel = (section) => {
    const track = section.querySelector("[data-lh-track]")
    const prev = section.querySelector("[data-lh-prev]")
    const next = section.querySelector("[data-lh-next]")
    if (!track || !prev || !next) return

    const slides = Array.from(track.querySelectorAll("[data-lh-slide]"))
    if (slides.length <= 1) {
      prev.hidden = true
      next.hidden = true
      return
    }

    const centerX = () => track.scrollLeft + track.clientWidth / 2

    const currentIndex = () => {
      const cx = centerX()
      let best = 0
      let bestDist = Infinity

      slides.forEach((s, i) => {
        const mid = s.offsetLeft + s.offsetWidth / 2
        const d = Math.abs(mid - cx)
        if (d < bestDist) {
          bestDist = d
          best = i
        }
      })

      return best
    }

    const updateNav = () => {
      const i = currentIndex()
      prev.hidden = i <= 0
      next.hidden = i >= slides.length - 1
    }

    const goTo = (i) => {
      const idx = clamp(i, 0, slides.length - 1)
      track.scrollTo({ left: slides[idx].offsetLeft, behavior: "smooth" })
    }

    prev.addEventListener("click", () => goTo(currentIndex() - 1))
    next.addEventListener("click", () => goTo(currentIndex() + 1))

    let raf = 0
    track.addEventListener("scroll", () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        updateNav()
      })
    })

    updateNav()
  }

  const initSection = (section) => {
    initAllCompares(section)
    setupHotspots(section)
    setupCarousel(section)
  }

  const boot = () => {
    document.querySelectorAll(".lh[data-section-id]").forEach(initSection)
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot)
  else boot()

  document.addEventListener("shopify:section:load", (e) => {
    const root = e.target && e.target.querySelector ? e.target.querySelector(".lh[data-section-id]") : null
    if (root) initSection(root)
  })
})()